import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { startupsAPI } from '../lib/api';
import { Search, Rocket, ExternalLink, Filter, X, MapPin } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export const StartupsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [industry, setIndustry] = useState(searchParams.get('industry') || 'all');
  const [stage, setStage] = useState(searchParams.get('stage') || 'all');

  const industries = [
    { value: 'all', label: 'All Industries' },
    { value: 'ai', label: 'AI & Automation' },
    { value: 'robotics', label: 'Robotics' },
    { value: 'saas', label: 'SaaS & Software' },
    { value: 'healthtech', label: 'Health Tech' },
    { value: 'cleantech', label: 'Clean Tech' },
    { value: 'creative', label: 'Creative Tech' },
  ];

  const stages = [
    { value: 'all', label: 'All Stages' },
    { value: 'seed', label: 'Seed' },
    { value: 'series-a', label: 'Series A' },
    { value: 'series-b', label: 'Series B' },
    { value: 'growth', label: 'Growth' },
    { value: 'acquired', label: 'Acquired' },
  ];

  useEffect(() => {
    const fetchStartups = async () => {
      setLoading(true);
      try {
        const params = {};
        if (search) params.search = search;
        if (industry && industry !== 'all') params.industry = industry;
        if (stage && stage !== 'all') params.stage = stage;
        const res = await startupsAPI.getAll(params);
        setStartups(res.data);
      } catch (err) {
        console.error('Error fetching startups:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStartups();
  }, [search, industry, stage]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (industry && industry !== 'all') params.set('industry', industry);
    if (stage && stage !== 'all') params.set('stage', stage);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch('');
    setIndustry('all');
    setStage('all');
    setSearchParams({});
  };

  const hasFilters = search || (industry && industry !== 'all') || (stage && stage !== 'all');

  return (
    <div data-testid="startups-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <span className="text-label text-muted-foreground mb-4 block">Directory</span>
          <h1 className="text-display text-4xl lg:text-5xl mb-4">Startup Directory</h1>
          <p className="text-muted-foreground max-w-2xl">
            Explore Portland's innovative startup ecosystem. From early-stage ventures 
            to growth companies across AI, robotics, SaaS, and more.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-border sticky top-16 z-40 bg-background">
        <div className="container-wide py-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search startups..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="startups-search"
              />
            </div>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger className="w-[180px]" data-testid="industry-select">
                <SelectValue placeholder="Industry" />
              </SelectTrigger>
              <SelectContent>
                {industries.map(ind => (
                  <SelectItem key={ind.value} value={ind.value}>
                    {ind.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger className="w-[150px]" data-testid="stage-select">
                <SelectValue placeholder="Stage" />
              </SelectTrigger>
              <SelectContent>
                {stages.map(s => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" data-testid="search-btn">Search</Button>
            {hasFilters && (
              <Button type="button" variant="ghost" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </form>
        </div>
      </section>

      {/* Results */}
      <section>
        <div className="container-wide py-12">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="border border-border p-6 animate-pulse">
                  <div className="w-12 h-12 bg-muted rounded-sm mb-4" />
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : startups.length > 0 ? (
            <>
              <p className="text-sm text-muted-foreground mb-6">
                {startups.length} {startups.length === 1 ? 'startup' : 'startups'} found
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {startups.map((startup, index) => (
                  <Link 
                    key={startup.id}
                    to={`/startups/${startup.id}`}
                    className="group animate-slide-up"
                    style={{ animationDelay: `${index * 50}ms` }}
                    data-testid={`startup-card-${startup.id}`}
                  >
                    <Card className="card-hover h-full">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 rounded-sm bg-muted flex items-center justify-center overflow-hidden">
                            {startup.logo_url ? (
                              <img 
                                src={startup.logo_url} 
                                alt={startup.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Rocket className="w-6 h-6 text-muted-foreground" />
                            )}
                          </div>
                          {startup.is_hiring && (
                            <span className="text-xs bg-green-500/10 text-green-600 dark:text-green-400 px-2 py-1 rounded-full font-mono">
                              Hiring
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold text-lg mb-1 group-hover:text-primary transition-colors">
                          {startup.name}
                        </h3>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="badge-category">{startup.industry}</span>
                          <span className="text-xs text-muted-foreground capitalize">{startup.stage}</span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                          {startup.description}
                        </p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {startup.location}
                          </span>
                          {startup.founded_year && (
                            <span>Founded {startup.founded_year}</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <Rocket className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="font-semibold text-lg mb-2">No startups found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Try adjusting your search or filters
              </p>
              <Button variant="outline" onClick={clearFilters}>
                Clear all filters
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
